<template>
	<div data-test-id="sku-insurance-modal">
		<CForm class="d-flex align-items-start mb-3" @submit.prevent="handleSubmit">
			<div class="flex-fill">
				<CInput
					v-model.trim="inputSKU"
					class="form-sku-input"
					:disabled="disabled"
					:is-valid="!$v.inputSKU.$error && !isNotFound && !isDuplicated && isValid && null"
					:label="label"
					:placeholder="placeholder"
					add-input-classes="form-get-sku-input"
					data-test-id="input-sku"
					@input="handleChange"
				>
					<template #invalid-feedback>
						<div
							v-if="isNotFound"
							class="invalid-feedback"
							data-test-id="error-item-not-found"
						>
							We can't find this SKU in the system
						</div>
						<div
							v-else-if="isDuplicated"
							class="invalid-feedback"
							data-test-id="error-item-duplicated"
						>
							This SKU is already added, please try another.
						</div>
						<div
							v-else-if="!isValid"
							class="invalid-feedback"
							data-test-id="error-item-invalid"
						>
							{{ invalidFeedback }}
						</div>
						<div
							v-else-if="$v.inputSKU.$error"
							class="invalid-feedback"
							data-test-id="error-item-invalid"
						>
							{{ $t('global.error.required') }}
						</div>
					</template>
				</CInput>
				<div v-if="description" class="typo-caption color-black-45 mb-2">
					{{ description }}
				</div>
			</div>
			<BaseDropDown
				:value="selectedType"
				class="dropdown-type"
				:options="INSURANCE_TYPES"
				label="label"
				placeholder="Select type"
				:is-valid="!$v.selectedType.$error"
				:invalid-feedback="$t('global.error.required')"
				@select="handleSelectType"
			/>
			<CButton
				:disabled="disabled || isSubmitting"
				type="submit"
				class="btn-add ml-2"
				color="secondary"
				data-test-id="add-sku-btn"
			>
				Add SKU
			</CButton>
		</CForm>
		<slot
			v-if="!skuList.length"
			name="no-item"
		>
		</slot>
		<ul
			v-else
			class="list-item"
		>
			<draggable
				data-test-id="insurance-group-sku"
				tag="li"
				:value="skuList"
				@input="handleUpdateOrder"
			>
				<SKUListItem
					v-for="item in skuList"
					:key="item.id"
					:thumbnail="item.thumbnail"
					:name="item.name"
					:sku="item.sku"
					:quantity="item.quantity"
					:type="item.type"
					@onChangeQty="handleChangeQty(item.sku, $event)"
					@onRemove="handleRemove(item.sku)"
				/>
			</draggable>
		</ul>
	</div>
</template>

<script>
import draggable from 'vuedraggable';

import { required } from 'vuelidate/lib/validators';
import { getProductBySKUAPI } from '../services/api/sku.api';
import { transformedProduct } from '../assets/js/transform/products';
import SKUListItem from './SKUListItem.vue';
import { INSURANCE_TYPES } from '../enums/insurances';

export default {
	name: 'FormGetProductByInsuranceSKU',
	components: {
		draggable,
		SKUListItem,
	},
	props: {
		label: {
			type: String,
			default: null,
		},
		originalSkuList: {
			type: Array,
			default: () => [],
		},
		skuList: {
			type: Array,
			default: () => [],
		},
		hasBorder: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		isValid: {
			type: Boolean,
			default: true,
		},
		invalidFeedback: {
			type: String,
			default: null,
		},
		placeholder: {
			type: String,
			default: 'Paste or type SKU number here',
		},
		description: {
			type: String,
			default: null,
		},
	},
	validations: {
		inputSKU: {
			required,
		},
		selectedType: {
			required,
		},
	},
	data() {
		return {
			INSURANCE_TYPES,
			isSubmitting: false,
			isNotFound: false,
			isDuplicated: false,
			inputSKU: '',
			selectedType: null,
		};
	},
	methods: {
		resetState() {
			this.inputSKU = '';
			this.isNotFound = false;
			this.isDuplicated = false;
		},
		handleChange() {
			this.isNotFound = false;
			this.isDuplicated = false;
		},
		handleChangeQty(sku, value) {
			this.resetState();
			const newSkuList = this.skuList.map((product) => {
				if (product.sku !== sku) {
					return product;
				}

				return { ...product, quantity: value };
			});

			this.$emit('onGetProduct', newSkuList);
		},
		handleRemove(sku) {
			this.resetState();
			const foundProduct = this.skuList.find((product) => product.sku === sku);
			this.$emit('onRemove', foundProduct);
		},
		handleReset() {
			this.resetState();
			this.$emit('update:skuList', this.originalSkuList);
			this.close();
		},
		async handleSubmit() {
			this.$v.$touch();
			if (this.$v.$invalid) {
				return;
			}
			const exists = this.skuList.find((product) => product.sku.toUpperCase() === this.inputSKU.toUpperCase());
			if (exists) {
				this.isDuplicated = true;
				return;
			}

			this.isNotFound = false;
			this.isSubmitting = true;

			try {
				const params = {
					q: this.inputSKU,
				};
				const { data } = await getProductBySKUAPI(params);
				const product = {
					...transformedProduct(data.data),
					type: this.selectedType.value,
				};
				this.$emit('onGetProduct', [
					product,
					...this.skuList,
				]);
				this.resetState();
			} catch {
				this.isNotFound = true;
			} finally {
				this.$v.$reset();
				this.isSubmitting = false;
			}
		},
		handleUpdateOrder(sortedList) {
			this.$emit('onUpdateOrder', sortedList);
		},
		handleSelectType(newType) {
			if (this.selectedType === newType) {
				return;
			}
			this.selectedType = newType;
		},
	},
};
</script>

<style lang="scss" scoped>
	::v-deep .input-group-text {
		padding-right: 0;
	}

	::v-deep .form-get-sku-input {
		margin-right: rem(100);
	}

	.form-sku-input {
		margin-bottom: rem(8);
	}

	.btn-add {
		min-width: 100px;
	}

	.list-item {
		padding: rem(20) rem(16) 0 rem(16);
		max-height: rem(320);
		overflow-x: hidden;
		overflow-y: auto;
	}

	.dropdown-type {
		width: 180px;
		margin-left: 8px;
	}
</style>
